"use client";

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { AuthDialog, type AuthMode } from "@/components/auth/AuthDialog";
import { getDictionary, normalizeLocale, type Locale } from "@/i18n/dictionaries";
import { normalizeProductSearchKey } from "@/lib/product-list-data";
import { HeaderCartLink } from "./HeaderCartLink";
import styles from "./layout.module.css";

type AuthSession = {
  status?: "authenticated" | "anonymous" | "unavailable";
  authenticated?: boolean;
  user?: {
    account?: string;
    name?: string;
  };
};

function switchHref(pathname: string, current: { toString(): string }, locale: Locale) {
  const next = new URLSearchParams(current.toString());
  next.set("lang", locale);
  return `${pathname}?${next.toString()}`;
}

export function Header() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = normalizeLocale(searchParams.get("lang"));
  const dictionary = getDictionary(locale);
  const [keyword, setKeyword] = useState(searchParams.get("key") || "");
  const [searchOpen, setSearchOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const [accountName, setAccountName] = useState("");
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const navItems = [
    { label: locale === "en-US" ? "Custom Jewelry" : "跨境定制", href: `/custom-jewelry?lang=${locale}` },
    { label: dictionary.nav.craft, href: `/art-upload?lang=${locale}` },
    { label: dictionary.nav.works, href: `/works?lang=${locale}` },
    { label: dictionary.nav.creative, href: `/diy?lang=${locale}` },
    { label: dictionary.nav.guide, href: `/design-guide?lang=${locale}` }
  ];
  const queryText = searchParams.toString();
  const returnUrl = queryText ? `${pathname}?${queryText}` : pathname;
  const quickSearchItems = dictionary.header.quickSearchItems || [];
  const quickSearchLinks = [
    { label: quickSearchItems[0], href: `/product-list?lang=${locale}` },
    { label: quickSearchItems[1], href: `/diy?lang=${locale}` },
    { label: quickSearchItems[2], href: `/product-list?lang=${locale}&category=31` },
    { label: quickSearchItems[3], href: `/product-list?lang=${locale}&category=30` },
    { label: quickSearchItems[4], href: `/design-guide?lang=${locale}` }
  ].filter((item): item is { label: string; href: string } => Boolean(item.label));
  const accountMenu = locale === "en-US"
    ? [
        { label: "My Orders", href: `/my-account/orders?lang=${locale}`, icon: "order" },
        { label: "My Models", href: `/my-account/models?lang=${locale}`, icon: "model" },
        { label: "My Studio", href: `/my-account/studio?lang=${locale}`, icon: "studio" },
        { label: "My Info", href: `/my-account/profile?lang=${locale}`, icon: "info" }
      ]
    : [
        { label: "\u6211\u7684\u8ba2\u5355", href: `/my-account/orders?lang=${locale}`, icon: "order" },
        { label: "\u6211\u7684\u6a21\u578b", href: `/my-account/models?lang=${locale}`, icon: "model" },
        { label: "\u6211\u7684\u5de5\u4f5c\u5ba4", href: `/my-account/studio?lang=${locale}`, icon: "studio" },
        { label: "\u6211\u7684\u4fe1\u606f", href: `/my-account/profile?lang=${locale}`, icon: "info" }
      ];

  useEffect(() => {
    let cancelled = false;

    async function loadSession() {
      try {
        const response = await fetch("/api/auth/session", { cache: "no-store" });
        const session = await response.json() as AuthSession;
        if (!cancelled) {
          if (session.status === "unavailable" || response.status === 503) {
            setSessionUnavailable(true);
            return;
          }
          setSessionUnavailable(false);
          setAccountName(session.authenticated ? session.user?.name || session.user?.account || "" : "");
        }
      } catch {
        if (!cancelled) {
          setSessionUnavailable(true);
        }
      }
    }

    function syncAccount(event?: Event) {
      const detail = event instanceof CustomEvent ? event.detail as { user?: { name?: string; account?: string }; account?: string } : undefined;
      if (detail?.user || detail?.account) {
        setAccountName(detail.user?.name || detail.user?.account || detail.account || "");
        setSessionUnavailable(false);
        setAccountOpen(false);
        return;
      }
      void loadSession();
    }

    void loadSession();
    window.addEventListener("auth:changed", syncAccount);
    return () => {
      cancelled = true;
      window.removeEventListener("auth:changed", syncAccount);
    };
  }, []);

  function searchHref(value: string) {
    const next = new URLSearchParams();
    next.set("lang", locale);
    const normalizedKey = normalizeProductSearchKey(value);
    if (normalizedKey) {
      next.set("key", normalizedKey);
    }
    return `/product-list?${next.toString()}`;
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSearchOpen(false);
    router.push(searchHref(keyword));
  }

  function openAuth(mode: AuthMode) {
    setSearchOpen(false);
    setAuthMode(mode);
  }

  async function logout() {
    const response = await fetch("/api/auth/logout", { method: "POST" }).catch(() => null);
    if (!response?.ok) {
      window.alert(locale === "en-US" ? "Sign out failed. Please try again." : "退出失败，请稍后重试。");
      return;
    }
    setAccountName("");
    setSessionUnavailable(false);
    setAccountOpen(false);
    window.dispatchEvent(new CustomEvent("auth:changed", { detail: { user: null } }));
    window.dispatchEvent(new Event("cart:refresh"));
    router.refresh();
  }

  return (
    <header className={styles.header}>
      <div className={styles.topBar}>
        <div className={styles.topActions}>
          <HeaderCartLink href={`/cart?lang=${locale}`} label={dictionary.header.cart} />
          <span className={styles.divider} />
          <form className={styles.searchForm} onSubmit={submitSearch}>
            <button className={styles.searchIcon} type="button" aria-label={dictionary.header.search} onClick={() => setSearchOpen(true)} />
            <input
              className={styles.searchInput}
              value={keyword}
              placeholder={dictionary.header.search}
              onFocus={() => setSearchOpen(true)}
              onChange={(event) => setKeyword(event.target.value)}
            />
          </form>
          {accountName ? (
            <div className={styles.accountWrap}>
              <button
                className={styles.accountNameButton}
                type="button"
                aria-expanded={accountOpen}
                onClick={() => setAccountOpen((value) => !value)}
              >
                <span className={styles.accountName}>{accountName}</span>
              </button>
              {accountOpen ? (
                <div className={styles.accountMenu}>
                  {accountMenu.map((item) => (
                    <Link key={item.href} href={item.href} onClick={() => setAccountOpen(false)}>
                      <span className={`${styles.accountMenuIcon} ${styles[`${item.icon}Icon`]}`} aria-hidden="true" />
                      {item.label}
                    </Link>
                  ))}
                  <button type="button" onClick={logout}>
                    <span className={`${styles.accountMenuIcon} ${styles.logoutIcon}`} aria-hidden="true" />
                    {dictionary.header.logout}
                  </button>
                </div>
              ) : null}
            </div>
          ) : sessionUnavailable ? (
            <span className={styles.sessionNotice} role="status">
              {locale === "en-US" ? "Account service unavailable" : "账户服务暂不可用"}
            </span>
          ) : (
            <>
              <button className={styles.topActionButton} type="button" onClick={() => openAuth("register")}>
                {dictionary.header.register}
              </button>
              <button className={styles.topActionButton} type="button" onClick={() => openAuth("login")}>
                {dictionary.header.login}
              </button>
            </>
          )}
          <nav className={styles.language} aria-label="Language switch">
            <Link className={locale === "zh-CN" ? styles.activeLanguage : ""} href={switchHref(pathname, searchParams, "zh-CN")}>
              {dictionary.language.zh}
            </Link>
            <span>/</span>
            <Link className={locale === "en-US" ? styles.activeLanguage : ""} href={switchHref(pathname, searchParams, "en-US")}>
              {dictionary.language.en}
            </Link>
          </nav>
        </div>
      </div>
      <div className={styles.logoBlock}>
        <Link href={`/?lang=${locale}`} className={styles.logoLink} aria-label={dictionary.brand}>
          <img className={styles.logoImage} src="/images/logo.png" alt={dictionary.brand} />
        </Link>
        <nav className={styles.nav} aria-label="Main navigation">
          {navItems.map((item) => (
            <a key={item.label} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
      </div>
      {searchOpen ? (
        <div className={styles.searchOverlay} role="dialog" aria-modal="true" aria-label={dictionary.header.quickSearch}>
          <form className={styles.searchPanel} onSubmit={submitSearch}>
            <div className={styles.searchPanelTop}>
              <button className={styles.searchPanelIcon} type="submit" aria-label={dictionary.header.search} />
              <input
                autoFocus
                value={keyword}
                placeholder={dictionary.header.searchPlaceholder}
                onChange={(event) => setKeyword(event.target.value)}
              />
              <button className={styles.searchClose} type="button" aria-label={dictionary.header.closeSearch} onClick={() => setSearchOpen(false)}>
                X
              </button>
            </div>
            <div className={styles.quickSearch}>
              <h2>{dictionary.header.quickSearch}</h2>
              <ul>
                {quickSearchLinks.map((item) => (
                  <li key={item.label}>
                    <Link href={item.href} onClick={() => setSearchOpen(false)}>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </form>
        </div>
      ) : null}
      {authMode ? (
        <AuthDialog
          locale={locale}
          labels={dictionary.authPage}
          mode={authMode}
          returnUrl={returnUrl}
          onClose={() => setAuthMode(null)}
        />
      ) : null}
    </header>
  );
}
